import { supabase } from "@/integrations/supabase/client";

import { handleSupabaseFunctionError } from "@/utils/supabaseFunctionsError";
import type {
  ProductsResponse,
  PurchasesResponse,
} from "@/api/types/paymentGateway";

export async function getProducts(): Promise<ProductsResponse> {
  const { data, error } = await supabase.functions.invoke(
    `api/stripe/products-catalog`,
    {
      method: "GET",
    }
  );

  await handleSupabaseFunctionError(error);

  if (
    !data ||
    typeof data !== "object" ||
    !Array.isArray((data as ProductsResponse).products)
  ) {
    console.error("Invalid products response:", data);
    throw new Error("Invalid response format from products catalog endpoint");
  }

  return data as ProductsResponse;
}

export async function getCustomerPortalUrl(): Promise<{ url: string }> {
  const { data, error } = await supabase.functions.invoke(
    `api/stripe/customer-portal`,
    {
      method: "GET",
    }
  );

  await handleSupabaseFunctionError(error);

  if (!data?.success || !data?.url) {
    throw new Error(data?.error || "Failed to create customer portal session");
  }

  return { url: data.url };
}

export async function getPurchases(): Promise<PurchasesResponse> {
  const { data, error } = await supabase.functions.invoke(
    `api/stripe/purchases`,
    {
      method: "GET",
    }
  );

  await handleSupabaseFunctionError(error);

  if (
    !data ||
    typeof data !== "object" ||
    !Array.isArray((data as PurchasesResponse).purchases)
  ) {
    console.error("Invalid purchases response:", data);
    throw new Error("Invalid response format from purchases endpoint");
  }

  return data as PurchasesResponse;
}

export async function getCheckoutSession(
  priceId: string,
  sessionId?: string,
  redirectUrl?: string
): Promise<{ checkoutSessionId: string }> {
  const params = new URLSearchParams({ priceId });
  if (sessionId) params.append("sessionId", sessionId);
  if (redirectUrl)
    params.append("redirectUrl", encodeURIComponent(redirectUrl));

  const { data, error } = await supabase.functions.invoke(
    `api/stripe/checkout-session?${params.toString()}`,
    {
      method: "GET",
    }
  );

  // Handle errors
  await handleSupabaseFunctionError(error);
  // Validate response format
  if (!data) {
    console.error("Invalid checkout session response:", data);
    throw new Error("Invalid response format from checkout session endpoint");
  }

  return data;
}
